import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedTime } from 'components/common/FormattedTime';
import { RelativeTarget } from 'components/common/RelativeTarget';
import { getMatterTypeName } from 'pages/matters/getMatterTypeName';
import useDropdown from 'hooks/useDropdown';
import styled, { css } from 'styled-components';

export function MatterResultItem({ matter, setSelected, selected }) {
  const {
    name,
    client: { name: clientName },
  } = matter;

  const handleClick = React.useCallback(
    e => {
      e.preventDefault();
      setSelected(selected ? undefined : { matter });
    },
    [setSelected, selected, matter],
  );

  return (
    <StyledMatterResultItem $selected={selected?.matter?.id === matter.id}>
      <button onClick={handleClick} type="button">
        <section>
          <div>
            <div className="client-name">{clientName}</div>
            <b>{name}</b>
          </div>
          <div>{getMatterTypeName(matter.matterType)}</div>
        </section>
      </button>
      {selected?.matter?.id === matter.id && (
        <FolderSelector
          matter={matter}
          setSelected={setSelected}
          selected={selected}
          onClick={handleClick}
        />
      )}
    </StyledMatterResultItem>
  );
}

function FolderSelector({ matter, onChange, setSelected, selected, onClick }) {
  const buttonRef = React.useRef();
  const [dropdown, toggle, onBlur, containerRef] = useDropdown();
  const newFolderRef = React.useRef();

  const handleSelectFolder = React.useCallback(
    (e, folder) => {
      toggle(e, false);
      setSelected(currentSelected =>
        currentSelected?.folder?.name === folder.name &&
        currentSelected?.folder?.id === folder.id
          ? { ...currentSelected, folder: undefined }
          : { ...currentSelected, folder },
      );
      onChange && onChange(e, folder);
    },
    [onChange, setSelected, toggle],
  );

  return (
    <>
      <StyledFolderButton onClick={toggle} ref={buttonRef}>
        {selected?.folder ? (
          <>
            <i className="far fa-folder-open" />{' '}
            {selected?.folder.name || <i>Unnamed Folder</i>}{' '}
          </>
        ) : (
          <>Put in Folder...</>
        )}
      </StyledFolderButton>
      {dropdown && (
        <RelativeTarget
          target={buttonRef}
          ref={containerRef}
          vAnchor="bottom"
          zIndex={120}
        >
          <ScrollCatcher onClick={toggle} />

          <StyledFolderOptions
            onBlur={onBlur}
            ref={ref => {
              if (ref) ref.focus();
            }}
          >
            <ol>
              <StyledFolderItem
                $selected={
                  selected?.folder?.name === 'New Folder' &&
                  !selected?.folder?.id
                }
              >
                <StyledFolderOptionButton
                  ref={newFolderRef}
                  onClick={e => handleSelectFolder(e, { name: 'New Folder' })}
                >
                  <i className="far fa-folder-open" /> <span>New Folder</span>
                </StyledFolderOptionButton>
              </StyledFolderItem>
              {matter.folders.map(folder => (
                <StyledFolderItem
                  key={folder.id}
                  $selected={folder.id === selected?.folder?.id}
                >
                  <StyledFolderOptionButton
                    onClick={e => handleSelectFolder(e, folder)}
                  >
                    <div>
                      <i className="far fa-folder-open" />
                      <span>
                        {folder.name || (
                          <i style={{ opacity: 0.5 }}>Unnamed Folder</i>
                        )}
                      </span>
                    </div>
                    <div>
                      Created{' '}
                      <FormattedTime time={folder.createdAt} format="MMM D" />
                    </div>
                  </StyledFolderOptionButton>
                </StyledFolderItem>
              ))}
            </ol>
          </StyledFolderOptions>
        </RelativeTarget>
      )}
    </>
  );
}

function ScrollCatcher({ children, onClick, onScroll, zIndex = 110 }) {
  return ReactDOM.createPortal(
    <>
      <div
        data-name="scrollCatcher"
        onClick={onClick}
        onScroll={onScroll}
        onKeyDown={e => void 0}
        role="none"
        style={{
          zIndex,
          height: '100vh',
          width: '100vw',
          top: 0,
          left: 0,
          position: 'absolute',
        }}
      />
      {children}
    </>,
    document.body,
  );
}

const StyledFolderOptionButton = styled.button`
  display: flex;
  width: 100%;
  text-align: left;
  padding: 12px 0 9px;
  font-family: 'Zilla Slab', serif;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.2;
  i + span {
    margin-left: 9px;
  }
  div {
    flex: 1 1 auto;

    &:last-child {
      flex: 0 1 auto;
      text-align: right;
      font-family: 'Zilla Slab', serif;
      font-size: 14px;
      font-weight: normal;
      font-style: italic;
      line-height: 1.29;
    }
  }
`;

const StyledFolderItem = styled.li`
  padding: 0 12px;
  &:hover {
    background: color-mix(
      in srgb,
      var(--COLOR_CEB_HEADER_BLUE) 2.5%,
      transparent
    );
  }
  & + & {
    button {
      border-top: solid 1px rgba(0, 0, 0, 0.1);
    }
  }
  ${({ $selected }) =>
    $selected &&
    css`
      background: color-mix(
        in srgb,
        var(--COLOR_CEB_HEADER_BLUE) 10%,
        transparent
      );
    `}
`;

const StyledFolderOptions = styled.div`
  background: white;
  transform: translateY(-8px);
  border-radius: 4px;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.15);
  border: solid 1px #d2d2d2;
  max-height: 10em;
  overflow: auto;
  width: 98%;
  margin: 0 auto;
  box-sizing: border-box;
  ol {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 0 6px;
    list-style-type: none;
  }
`;

const StyledMatterResultItem = styled.li`
  border-radius: 3px;
  overflow: hidden;
  border-bottom: solid 1px rgba(0, 0, 0, 0.1);
  button {
    display: block;
    padding: 9px 15px 9px;
    font-size: 15px;
    font-family: 'Zilla Slab', serif;
    text-align: left;
    width: 100%;
    &:hover {
      background-color: rgba(240, 238, 235, 0.5);
    }
    > section {
      display: flex;
      align-items: center;
      > div {
        flex: 1 1 auto;
        &:last-child {
          flex: 0 1 auto;
        }
        .client-name {
          margin-bottom: 4px;
        }
      }
    }
  }
  ${({ $selected }) =>
    $selected &&
    css`
      color: white;
      background: var(--COLOR_CEB_HEADER_BLUE);
    `}
`;

const StyledFolderButton = styled.button`
  color: #b7d7ea;
  i {
    margin-right: 4px;
  }
`;
